import { db } from '../../firebase.js';
import {
  doc, getDoc,
  collection, serverTimestamp, runTransaction, getDocs, orderBy, query
} from "firebase/firestore";

/**
 * ============================================================================
 * PC POINTS / CREDITS — COMPLIANCE GUARDRAIL (do not violate without legal sign-off)
 * ============================================================================
 * PokerCity points are a CLOSED-LOOP system. They keep PokerCity clear of
 * gambling/lottery law only because the exit-to-cash door is welded shut.
 *
 *   R1  EARNED (free, loyalty) or PURCHASED (paid, SaaS) are both allowed.
 *       Selling credits to use features is permitted software commerce.
 *   R2  NO CHANCE. No draws, spin-to-win, loot boxes, or buying a chance at a
 *       variable reward. Purchases are FIXED price for a KNOWN feature only.
 *   R3  NO CASH-OUT. No balance — earned or purchased — may EVER be converted
 *       to money, redeemed for monetary value, withdrawn, or refunded as cash.
 *       This is the load-bearing rule. Do not add a cash-out path here.
 *   R4  Tipping transfers points as recognition only; never moves money and a
 *       tip can never be cashed out by the recipient.
 *   R5  ANY change to R1–R4 — especially opening a cash-out path — requires
 *       legal sign-off FIRST and becomes its own separate, lawyer-gated product.
 *
 * Reference: "PokerCity — Compliance & Legal Framework", Section 3.
 * ============================================================================
 */

export type TokenTransactionType =
  | 'signup_bonus'
  | 'event_created'
  | 'event_verified'
  | 'player_invited'
  | 'td_invited'
  | 'game_suggested'
  | 'purchased'
  | 'bonus';

class TokenService {
  private dbName = process.env.DB === 'TEST' ? 'users_test' : 'users';
  private SIGNUP_BONUS = 100;
  private EVENT_COST = 25;

  async getBalance(uid: string): Promise<number> {
    try {
      const docRef = doc(db, this.dbName, uid);
      const docSnap = await getDoc(docRef);
      return docSnap.data()?.token_balance ?? 0;
    } catch (error) {
      console.error('Error getting token balance:', error);
      return 0;
    }
  }

  async awardSignupBonus(uid: string): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, this.dbName, uid);
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists() || userSnap.data()?.signup_bonus_awarded !== true) {
          transaction.update(userRef, { token_balance: this.SIGNUP_BONUS, signup_bonus_awarded: true });
          const txRef = doc(collection(db, this.dbName, uid, 'token_transactions'));
          transaction.set(txRef, {
            type: 'signup_bonus',
            amount: this.SIGNUP_BONUS,
            balance_after: this.SIGNUP_BONUS,
            description: 'Welcome bonus — 100 PC tokens',
            created_at: serverTimestamp()
          });
        }
      });
    } catch (error) {
      console.error('Error awarding signup bonus:', error);
    }
  }

  // Award referrer 25 tokens when referred user creates first event
  async processReferrerReward(newUserUid: string): Promise<void> {
    try {
      const userRef = doc(db, this.dbName, newUserUid);
      const userSnap = await getDoc(userRef);
      const data = userSnap.data();
      // Only fire on first event and if referred
      if (!data?.referred_by || data?.referrer_rewarded) return;
      const referrerUid = data.referred_by;

      // Find referrer by UID — check referral cap (500)
      const referrerRef = doc(db, this.dbName, referrerUid);
      const referrerSnap = await getDoc(referrerRef);
      const referrerData = referrerSnap.data();
      const referrerEarned = referrerData?.tokens_earned_total ?? 0;
      if (referrerEarned >= 500) return; // Cap reached

      // Award 25 tokens to referrer
      await this.awardTokens(referrerUid, 25, 'player_invited',
        'Referral reward — invited player created their first event');

      // Update referrer total earned
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(referrerRef, {
        tokens_earned_total: referrerEarned + 25
      });

      // Mark reward as paid on new user doc
      await updateDoc(userRef, { referrer_rewarded: true });

    } catch (error) {
      console.error('Error processing referrer reward:', error);
    }
  }

  // GUARDRAIL (R3): this is a SPEND only — never refund, reverse, or pay out as cash.
  // Tokens leave the balance to unlock a feature and do not return as money.
  async deductEventTokens(uid: string): Promise<{ success: boolean; balance: number; error?: string }> {
    try {
      let newBalance = 0;
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, this.dbName, uid);
        const userSnap = await transaction.get(userRef);
        const currentBalance = userSnap.data()?.token_balance ?? 0;
        if (currentBalance < this.EVENT_COST) throw new Error('INSUFFICIENT_TOKENS');
        newBalance = currentBalance - this.EVENT_COST;
        transaction.update(userRef, { token_balance: newBalance });
        const txRef = doc(collection(db, this.dbName, uid, 'token_transactions'));
        transaction.set(txRef, {
          type: 'event_created',
          amount: -this.EVENT_COST,
          balance_after: newBalance,
          description: 'Event created — 25 PC tokens',
          created_at: serverTimestamp()
        });
      });
      return { success: true, balance: newBalance };
    } catch (error: any) {
      if (error.message === 'INSUFFICIENT_TOKENS') {
        return { success: false, balance: 0, error: 'Insufficient PC tokens. Please top up to create more events.' };
      }
      return { success: false, balance: 0, error: 'Token deduction failed.' };
    }
  }

  async deductTokensForAction(uid: string, amount: number, type: TokenTransactionType, description: string): Promise<{ success: boolean; error?: string }> {
    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, this.dbName, uid);
        const userSnap = await transaction.get(userRef);
        const currentBalance = userSnap.data()?.token_balance ?? 0;
        if (currentBalance < amount) throw new Error('INSUFFICIENT_TOKENS');
        const newBalance = currentBalance - amount;
        transaction.update(userRef, { token_balance: newBalance });
        const txRef = doc(collection(db, this.dbName, uid, 'token_transactions'));
        transaction.set(txRef, {
          type, amount: -amount, balance_after: newBalance,
          description, created_at: serverTimestamp()
        });
      });
      return { success: true };
    } catch (error: any) {
      if (error.message === 'INSUFFICIENT_TOKENS') {
        return { success: false, error: 'Insufficient PC tokens.' };
      }
      return { success: false, error: 'Token deduction failed.' };
    }
  }

  async awardTokens(uid: string, amount: number, type: TokenTransactionType, description: string): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, this.dbName, uid);
        const userSnap = await transaction.get(userRef);
        const currentBalance = userSnap.data()?.token_balance ?? 0;
        const newBalance = currentBalance + amount;
        transaction.update(userRef, { token_balance: newBalance });
        const txRef = doc(collection(db, this.dbName, uid, 'token_transactions'));
        transaction.set(txRef, {
          type, amount, balance_after: newBalance, description,
          created_at: serverTimestamp()
        });
      });
    } catch (error) {
      console.error('Error awarding tokens:', error);
    }
  }

  async getTransactions(uid: string) {
    try {
      const txRef = collection(db, this.dbName, uid, 'token_transactions');
      const q = query(txRef, orderBy('created_at', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error getting transactions:', error);
      return [];
    }
  }
}

export default TokenService;
